import { useEffect, useState } from 'react'

import { Detection } from 'components/exceptions/types'
import { blobToBase64, getExceptionImageBlobFromPath, resolveVisionGsUri } from 'utils/file'

// Session cache so paging the grid doesn't re-download every Firebase blob.
const resolvedCache = new Map<string, Promise<string>>()

// Idempotent: already-resolved http/data/blob must not fall through to bucket path.
const isRenderable = (uri: string): boolean => uri.startsWith('http') || uri.startsWith('data:') || uri.startsWith('blob:')

// gs:// → vision bucket; file:// → blank; else private uploads path → getBlob → base64.
const resolveMediaUri = (uri: string): Promise<string> => {
  if (isRenderable(uri)) return Promise.resolve(uri)
  if (uri.startsWith('gs://')) return resolveVisionGsUri(uri)
  if (uri.startsWith('file:')) return Promise.resolve('')
  return getExceptionImageBlobFromPath(uri, 'default').then(blobToBase64) as Promise<string>
}

/** Resolve one media uri outside React — same session cache the hooks use. */
export const resolveOnce = (uri: string): Promise<string> => {
  const hit = resolvedCache.get(uri)
  if (hit) return hit
  const pending = resolveMediaUri(uri).catch(() => '')
  resolvedCache.set(uri, pending)
  return pending
}

/**
 * Resolves one exception-media uri for `<img>` / background use, memoized per uri.
 * Raw `gs://` or private-bucket paths render broken without this.
 */
export const useResolvedMediaUrl = (uri: string | undefined): string => {
  const key = (uri ?? '').trim()
  const [resolved, setResolved] = useState(isRenderable(key) ? key : '')

  useEffect(() => {
    if (!key) {
      setResolved('')
      return undefined
    }
    let cancelled = false
    resolveOnce(key).then(url => {
      if (!cancelled) setResolved(typeof url === 'string' ? url : '')
    })
    return () => {
      cancelled = true
    }
  }, [key])

  return resolved
}

/**
 * Best renderable thumbnail: http `thumbnailUrl`, then resolved gs:// / bucket path,
 * then first capture. Resolve at render so the list query doesn't block on Firebase.
 */
export const useResolvedVisionThumbnail = (detection: Detection): string => {
  const resolved = useResolvedMediaUrl(detection.thumbnailGsUri || detection.thumbnailImagePath)
  return detection.thumbnailUrl || resolved || detection.screenshots[0]?.url || ''
}
